import React, {
 FunctionComponent, useEffect, useState, Fragment, useRef,
 useCallback,
 useMemo
} from 'react';
import cloneDeep from 'lodash.clonedeep';
import { useTranslation } from 'react-i18next';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import {
  GetRowIdParams, GridReadyEvent, IServerSideGetRowsParams,
  RowNode,
  SortModelItem
} from '@ag-grid-enterprise/all-modules';
import i18n from 'i18n';
import { RootState } from 'data/root.reducers';
import { GridSettings, WithAuditDetails, AuditDetails } from 'data/types';
import '../../alarm.scss';
import { Popover2, Tooltip2 } from '@blueprintjs/popover2';
import { DateRange, DateRangePicker, IDateRangeShortcut } from '@blueprintjs/datetime';
import {
  AnchorButton, Button, Classes, InputGroup, Intent,
  Spinner,
  SpinnerSize,
  Label
} from '@blueprintjs/core';

import moment from 'moment';
import { AgGridReact } from '@ag-grid-community/react';
import { activeAlarmsColumnDefs } from 'routes/Alarm/Alarm.types';
import { getActiveAlarms, suppressedAlarms } from 'data/alarm/alarm.api';
import { ActiveAlarm } from 'data/alarm/alarm.types';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import getSuppressedAlarmPayload from 'routes/Alarm/utils/getSuppressedAlarmPayload';
import ServerSideDatasource from 'shared/commonGrid/serverSideDatasource';

type RouterParams = {
  history: string;
};

const mapState = (state: RootState) => ({
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
});

const mapDispatch = {};

const connector = connect(mapState, mapDispatch);

const PAGE_SIZE = 100;
const CACHE_BLOCK_SIZE = 100;

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>;

export type Props = PropsFromRedux & PropsFromRouter;

export const ActiveAlarmList: FunctionComponent<Props> = ({
  history,
  isAuditEnabled,
  userId,
  userName
}: Props) => {
  const gridRef = useRef<any>(null);
  const startOfDay = new Date();
  startOfDay.setHours(0, 0, 0, 0);

  const endOfDay = new Date();
  endOfDay.setHours(23, 59, 59, 999);
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>([startOfDay, endOfDay]);
  const [selectedShortcutIndex, setSelectedShortcutIndex] = useState<number>(0);
  const [dateRangePopupOpen, setDateRangePopupOpen] = useState<boolean>(false);
  const [dateRangeButtonText, setDateRangeButtonText] = useState<string>('Today');
  const [selectedActiveAlarms, setSelectedActiveAlarms] = useState<ActiveAlarm[]>([]);

  const getServerSideDataSource = useCallback(() => {
    return {
      getRows(params: IServerSideGetRowsParams) {
        const { request } = params;
        const updateSortModel = request.sortModel.reduce<SortModelItem[]>((result, sortModel) => {
          if (sortModel.colId === 'timestamp') {
            result.push(
              { colId: 'date', sort: sortModel.sort },
              { colId: 'time', sort: sortModel.sort }
            )
          } else {
            result.push(sortModel);
          }
          return result;
        }, [])
        request.sortModel = updateSortModel;
        const dataSource = new ServerSideDatasource({});
        const options = dataSource.buildParamsFromJsonForXREST(request);
        const queryParams = new URLSearchParams(options);
        const dateRangeFilterCriteria = buildDateRangeFilterCriteria(selectedDateRange);
        const body = queryParams.get('$filter') != null ? `${options} and ${dateRangeFilterCriteria}` : `${options}&$filter=${dateRangeFilterCriteria}`;
        getActiveAlarms(body)
          .then((r) => {
            const rowData = r.data?.data || [];
            const rowCount = r.data?.metadata?.recordsInView || 0;
            params.success({ rowData, rowCount });
          })
          .catch((r) => {
            params.fail();
          });
      }
    };
  }, [selectedDateRange]);

  const buildDateRangeFilterCriteria = (
    dateRange: DateRange,
  ): string => {
    const thenextDate = new Date(); // get the next date to compare since db will compare time at 00:00
    if (dateRange[1]) {
      thenextDate.setDate(dateRange[1].getDate() + 1);
    }
    return `(date ge '${moment(dateRange[0]).format('YYYY-MM-DD')}') and (date le '${moment(dateRange[1]).format('YYYY-MM-DD')}')`;
  };

  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.setServerSideDatasource(getServerSideDataSource());
    }
  }, [getServerSideDataSource]);

  const onGridReady = useCallback((grid: GridReadyEvent) => {
    grid.columnApi.applyColumnState({
      state: [{ colId: 'timestamp', sort: 'desc', sortIndex: 0 }],
      defaultState: { sort: null },
    })
    grid.api.setServerSideDatasource(getServerSideDataSource());
  }, []);

  const handleShortcutChange = (shortcut: IDateRangeShortcut, index: number) => {
    setSelectedShortcutIndex(index);
    setDateRangeButtonText(shortcut.label);
  };

  const handleDateChange = (dateRange: DateRange) => {
    setSelectedDateRange(dateRange);
    if (!dateRange[0] || !dateRange[1]) {
      return;
    }
    if (gridRef.current) {
      setDateRangeButtonText(`${moment(dateRange[0]).format('ll')} - ${moment(dateRange[1]).format('ll')}`);
      setDateRangePopupOpen(false);
    }
  };

  const handleInteraction = (isOpen: boolean) => {
    setDateRangePopupOpen(isOpen);
  }

  const handleRefresh = () => {
    refreshTable();
  };

  const refreshTable = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.refreshServerSideStore({ purge: true });
      gridRef.current.api.deselectAll();
    }
  }

  const onSuppressAlarm = () => {
    suppressedAlarms(
      getSuppressedAlarmPayload(
        isAuditEnabled,
        userId,
        userName,
        selectedActiveAlarms
      )
    ).then(() => {
      refreshTable();
      setSelectedActiveAlarms([]);
    });
  }

  const onSelectionChanged = () => {
    const selectedRows = gridRef.current.api.getSelectedRows();
    gridRef.current.api.forEachNode((rowNode: RowNode, index: number) => {
      console.log(rowNode);
      console.log(index);
    });
    console.log(selectedRows);
    setSelectedActiveAlarms(selectedRows.slice());
  }

  const getRowId = (params: GetRowIdParams) => {
    return `${params.data.alarmid}-${params.data.instance}`;
  }

  return (
    <div className="row alarm-table-container" style={{ height: 'inherit' }}>
      <div className="formPanel">
        <h2 className="formPanelTitle">Active Alarms</h2>
      </div>

      <div className="col-md-12" style={{ height: 'inherit' }}>
        <div className="form-flex-row-css">
          <div>
            <Popover2
              interactionKind="click"
              placement="bottom"
              popoverClassName={Classes.POPOVER_CONTENT_SIZING}
              isOpen={dateRangePopupOpen}
              onInteraction={handleInteraction}
              minimal
              content={(
                <DateRangePicker
                  allowSingleDayRange
                  selectedShortcutIndex={selectedShortcutIndex}
                  value={selectedDateRange}
                  onChange={handleDateChange}
                  onShortcutChange={handleShortcutChange}
                />
              )}
            >
              <AnchorButton
                icon="calendar"
                rightIcon="caret-down"
                intent={Intent.PRIMARY}
                text={dateRangeButtonText}
              />
            </Popover2>
          </div>
          {/* Flex Spacer */}
          <div className="col-sm" />
          {selectedActiveAlarms.length > 0 && (
            <div>
              <Button
                className="menu-button"
                text={i18n.t('common.button.suppressAlarm', { suppressCount: selectedActiveAlarms.length })}
                onClick={onSuppressAlarm}
              />
            </div>
          )}
          <div>
            <AnchorButton
              icon="refresh"
              intent={Intent.NONE}
              text="Refresh"
              onClick={handleRefresh}
            />
          </div>
        </div>
        <div className="ag-theme-balham common-grid-container">
          <AgGridReact
            ref={gridRef}
            columnDefs={activeAlarmsColumnDefs}
            pagination
            paginationPageSize={PAGE_SIZE}
            rowSelection="multiple"
            rowModelType="serverSide"
            serverSideStoreType="partial"
            cacheBlockSize={CACHE_BLOCK_SIZE}
            onGridReady={onGridReady}
            suppressRowClickSelection
            onSelectionChanged={onSelectionChanged}
            getRowId={getRowId}
          />
        </div>
      </div>
    </div>
  );
};

export default withRouter(connector(ActiveAlarmList));
